import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthGuard, AuthService } from '@auth0/auth0-angular';
import { OrganizationManager } from '../managers/organization.manager';
import { LoggingService } from '../../../../modules/src/lib/core/services/logging.service';
import { BillingManager } from '../managers/billing.manager';
import { OrganizationLicenseStatusEnum } from '../enums/organization-license-status.enum';
import { OnlyLoginGuard } from './only-login.guard';
import { InviteManager } from '../managers/invite.manager';

@Injectable()
export class AccessGuard extends OnlyLoginGuard {
  constructor(
    protected inviteManager: InviteManager,
    protected authGuard: AuthGuard,
    protected billingManager: BillingManager,
    protected authService: AuthService,
    protected router: Router,
    protected organizationManager: OrganizationManager,
    protected loggingService: LoggingService,
  ) {
    super(authGuard, authService, router, organizationManager, inviteManager, loggingService);
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return new Promise(async (resolve, reject) => {
      const canActivate = await super.canActivate(route, state);

      if (canActivate === false) {
        return resolve(false);
      }

      // If you don’t have an organization, go to /onboarding/organization/create
      const organization = await this.organizationManager.getCurrent();
      if (organization === null) {
        // If there are no organization associated, check if you have any invites
        const invites = await this.inviteManager.getForCurrentUser();
        if (invites.length > 0) {
          this.loggingService.debug('[Guard:\'access\'] - User has pending invites, redirecting to /onboarding/invites');
          return resolve(this.router.parseUrl('/onboarding/invites'));
        }

        this.loggingService.debug('[Guard:\'access\'] - User doesn\'t have any organization, redirecting to /onboarding/organization/create');
        return resolve(this.router.parseUrl('/onboarding/organization/create'));
      }

      // If you don’t have an active license, go to /onboarding/subscription/create
      // If your license is suspended, go to /subscription/suspended
      const licenseStatus = await this.billingManager.getLicenseStatus();
      switch (licenseStatus) {
        case OrganizationLicenseStatusEnum.Suspended:
          this.loggingService.debug('[Guard:\'access\'] - Subscription is suspended, redirecting to /organization/billing/subscription/suspended');
          return resolve(this.router.parseUrl('/organization/billing/subscription/suspended'));
        case OrganizationLicenseStatusEnum.Unlicensed:
          this.loggingService.debug('[Guard:\'access\'] - Organization doesn\'t have an active subscription, redirecting to /onboarding/subscription/create');
          return resolve(this.router.parseUrl('/onboarding/subscription/create'));
      }

      // If the route has a specific permission flag, check to see if the user has it.
      if (route.data.hasOwnProperty('organizationFlag') === true) {
        try {
          const hasPermission = await this.organizationManager.hasPermission(route.data.organizationFlag);
          if (hasPermission === false) {
            return resolve(this.router.parseUrl('/onboarding/upgrade'));
          }
        } catch (e) {
          this.loggingService.debug('[Guard:\'access\'] - Organization doesn\'t have the permissions, redirecting to /onboarding/upgrade');
          return resolve(this.router.parseUrl('/onboarding/upgrade'));
        }

      }

      // Else, you have access
      return resolve(true);
    });
  }

}
